import { call, put, select, all } from 'typed-redux-saga';
import { SagaIterator } from 'redux-saga';
import i18n from 'i18next';
import { EOperationStatus, IAction } from 'types';
import { routes } from 'routes';
import { parseJSONSafe } from 'utils';
// import { intersection } from 'lodash';
import {
    getOmjsViewInstance,
    getOmjsProjectInstance,
    getCachedPubContainers,
    getCachedFullContainers,
    getMapParams, getOperationData, getOperationStatus
} from 'selectors';
import axios from 'axios';

import { isMobileOnlyFixed } from 'utils/is-fixed-ismobileonly';
import {
    Service, bindActionsTyped, leading, every
} from '../service-utils';
import { ContainersService, IFilter } from '../containers/containers-service';
import { operation, OperationsService } from '../operations/operations-service';
import { MapService } from '../map/map-service';
import { omjsActions as A } from './omjs-reducer-actions';
import { IState } from '../root-reducer';
import { apiRequest } from '../../api';
import { AppService } from '../app/app-service';

export const SITE_TYPE_CUSTOM_FILTER_ID = -1000;

interface ISetInstancesPayload {
    view: omjs.View;
    layers: number[];
}

const getChilds = (containers, parent, deep = false) => {
    let childs = containers.filter(cont => cont.get('parent_id') === parent?.get('id'));

    if (deep) {
        for (let child of childs) {
            childs = [ ...childs, ...getChilds(containers, child, deep) ];
            // if (child.get('type') === 'folder') {
            //
            // }
        }
    }
    return childs;
};

const getContainerById = (containers, id) => containers.find(cont => cont.get('id') === id);

const updateParentSelected = (containers, container, selected) => {
    let parentId = container.get('parent_id');

    while (parentId) {
        let parent = getContainerById(containers.getArray(), parentId);
        parentId = parent.get('parent_id');
        if (selected) {
            parent.set('selected', selected);
        } else {
            let parentChilds = getChilds(containers.getArray(), parent);
            let selectedCount = parentChilds.reduce((acc, item) => (item.get('selected') ? acc + 1 : acc), 0);
            if ((selected && selectedCount > 0) || (!selected && selectedCount === 0)) {
                parent.set('selected', selected);
            }
        }
    }
};

export class OmjsService extends Service {
    static serviceName = 'OmjsService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    static* projectGetContainers(project: omjs.Project, options?) {
        return yield new Promise((resolve, reject) => {
            project.getContainers(options)
                .done(resolve)
                .fail(reject);
        });
    }

    static* loadProjectLayer(layerCode: string) {
        let project = yield* select(getOmjsProjectInstance);
        return yield new Promise((resolve, reject) => {
            project.loadLayer(layerCode)
                .done(resolve)
                .fail(reject);
        });
    }

    static* loadProjectObject(layer: string | omjs.ProjectLayer, objectId: number, options?) {
        let project = yield* select(getOmjsProjectInstance);
        let layerCode: string = '';

        if (typeof layer === 'string') {
            layerCode = layer;
        } else {
            layerCode = layer.get('code');
            project = layer.project;
        }

        return yield new Promise((resolve, reject) => {
            project.loadObject(layerCode, objectId, options)
                .done(resolve)
                .fail(reject);
        });
    }

    static* loadLayerObjects(layerCode: string, options?) {
        let project = yield* select(getOmjsProjectInstance);

        return yield new Promise((resolve, reject) => {
            project.loadObjects(layerCode, options)
                .done(resolve)
                .fail(reject);
        });
    }

    static* loadProjectLayerStructure(layer: string | omjs.ProjectLayer, options?) {
        let project = yield* select(getOmjsProjectInstance);
        let layerCode: string = '';

        if (typeof layer === 'string') {
            layerCode = layer;
        } else {
            layerCode = layer.get('code');
            project = layer.project;
        }

        return yield new Promise((resolve, reject) => {
            project.loadStructure(layerCode, options)
                .done(resolve)
                .fail(reject);
        });
    }

    static* loadFiltersConfig() {
        const filtersConfig = {};
        const filtersLayer = isMobileOnlyFixed ? 'm_filter' : 'filter';

        const filters: { [key: string]: any }[] = yield call(OmjsService.loadLayerObjects, filtersLayer, {
            raw: true,
            fields: '*',
            sort: 'sort'
        });

        filters.forEach((filter) => {
            let {
                folder_code,
                settings_filter = {},
                settings_objects = {},
                name,
                type
            } = filter.fields;

            if (typeof settings_filter === 'string' && settings_filter.length > 0) {
                settings_filter = parseJSONSafe(settings_filter);
            } else {
                settings_filter = {};
            }

            if (typeof settings_objects === 'string' && settings_objects.length > 0) {
                settings_objects = parseJSONSafe(settings_objects);
            } else {
                settings_filter = {};
            }

            if (!(folder_code in filtersConfig)) {
                filtersConfig[folder_code] = [];
            }
            const fConfig = {
                id: filter.id,
                name,
                type,
                settingsFilter: settings_filter,
                settingsObjects: settings_objects
            };
            filtersConfig[folder_code].push(fConfig);
        });

        OmjsService.patchCustomFilters(filtersConfig);

        return filtersConfig;
    }

    static patchCustomFilters(filtersConfig: Record<string, IFilter[]>) {
        if (!filtersConfig['plots']) {
            filtersConfig['plots'] = [];
        }
        filtersConfig['plots'].push({
            'id': SITE_TYPE_CUSTOM_FILTER_ID,
            'is_custom': true,
            'name': i18n.t('Тип площадки'),
            'type': 'list_text',
            'settingsFilter': {},
            'settingsObjects': {
                'sites_1': 'type_site_maps',
                'sites_2': 'type_site_maps',
                'sites_3': 'type_site_maps',
                'sites_4': 'type_site_maps'
            },
            'value': null,
            'values': [
                i18n.t('Гринфилд'),
                i18n.t('Браунфилд'),
                i18n.t('Предложения для девелоперов'),
                i18n.t('Туристско-рекреационная')
            ]
        });
    }

    // static* login(login: string, password: string) {
    //     let project = yield* select(getOmjsProjectInstance);
    //     return yield new Promise<{ token: string }>((resolve, reject) => {
    //         project.login(login, password)
    //             .done(resolve)
    //             .fail(reject);
    //     });
    // }

    static* logout() {
        let project = yield* select(getOmjsProjectInstance);
        return yield new Promise((resolve, reject) => {
            project.logout()
                .done(resolve)
                .fail(reject);
        });
    }

    // static* isTokenValid(token: string, set_token_if_authorized:boolean = false) {
    //     let project = yield* select(getOmjsProjectInstance);
    //     return yield new Promise((resolve, reject) => {
    //         project.isAuthorized(token, set_token_if_authorized)
    //             .done(resolve)
    //             .fail(reject);
    //     });
    // }

    @leading
    @operation('OMJS_READY')
    static* setInstances(action: IAction<ISetInstancesPayload>) {
        const { view, layers } = action.payload;
        const baseLayer = view.getBaseLayer();
        const baseMapId = baseLayer.currentSource;

        yield call(OperationsService.pending, 'DATA_LOAD');

        const pubContainers = yield call(OmjsService.projectGetContainers, view.getProject(), {
            type: 'publish',
            returnBbox: 0,
            bboxSR: 3857,
            with_gen_plans: i18n.language === 'ru' ? 1 : 0
        });

        const fullContainers = yield call(OmjsService.projectGetContainers, view.getProject(), {
            type: 'full',
            returnBbox: 0,
            returnCss: 0,
            with_gen_plans: i18n.language === 'ru' ? 1 : 0
        });

        yield put(A.setViewInstance(view));

        yield* call(AppService.loadUserInfo);

        // Собираем информацию по генпланам
        if (i18n.language === 'ru') {
            // --> Временно убираем генпланы, потом нужно удалить
            const { config, authToken } = yield* select((state: IState) => state.app);
            const pubArr = pubContainers.getArray();
            const fullArr = fullContainers.getArray();
            const pubParent = pubArr.find(cont => cont.get('code') === 'gen_plans');
            const fullParent = fullArr.find(cont => cont.get('code') === 'gen_plans');

            let garbage = getChilds(pubArr, pubParent, true);

            let settlementsId = 0;
            const cleanPubContainers = pubArr.filter(c => {
                if (c.get('code') === 'gen_plany_koordinaty') {
                    settlementsId = c.get('id');
                }
                return !garbage.includes(c) || c.get('code') === 'gen_plany_koordinaty';
            });

            const garbageIds = garbage.map(c => c.get('id'));
            const cleanLayersCfg = config.publication.layers_config.filter(cfg => (
                !garbageIds.includes(cfg.containerId) || cfg.containerId === settlementsId
            ));

            config.publication.layers_config.length = 0;
            config.publication.layers_config.push.apply(config.publication.layers_config, cleanLayersCfg);

            garbage = getChilds(fullArr, fullParent, true);
            const cleanFullContainers = fullArr.filter(c => (
                !garbage.includes(c) || c.get('code') === 'gen_plany_koordinaty'
            ));

            pubArr.length = 0;
            pubArr.push.apply(pubArr, cleanPubContainers);

            fullArr.length = 0;
            fullArr.push.apply(fullArr, cleanFullContainers);
            // <--

            // yield call(OmjsService.getGenPlanesData, pubContainers.getArray(), fullContainers.getArray());
        }

        const changedContainers = {};
        if (layers.length > 0) {
            pubContainers.getArray().forEach((cont) => {
                if (cont.get('type') !== 'folder') {
                    const inUrl = layers.includes(cont.get('id'));
                    if (cont.get('selected') !== inUrl) {
                        cont.set('selected', inUrl);
                        changedContainers[cont.get('id')] = inUrl;
                        updateParentSelected(pubContainers, cont, inUrl);
                    }
                }
            });

            fullContainers.getArray().forEach((cont) => {
                if (cont.get('id') in changedContainers) {
                    cont.set('selected', changedContainers[cont.get('id')]);
                }
            });
        }

        yield* call(OperationsService.updateData, 'MAP_STATE', { baseMapId });
        yield call(OmjsService.loadHints);
        const filtersConfig = yield* call(OmjsService.loadFiltersConfig);
        yield* call(ContainersService.setFiltersConfig, filtersConfig);
        yield* call(ContainersService.init, view.getProject().getCachedPubContainers());

        const mapParams = yield* select(getMapParams);
        yield call(MapService.setActiveLayers, mapParams.activeRasterLayers, mapParams.activeVectorLayers, []);
        routes.setMapParams({
            center: mapParams.center,
            zoom: mapParams.zoom,
            layers: [ ...mapParams.activeRasterLayers, ...mapParams.activeVectorLayers ].join(',')
        });
    }

    static* loadHints() {
        const fullContainers = yield* select(getCachedFullContainers);
        const hintsCont = fullContainers.find(c => c.get('code') === 'hints');
        hintsCont['loadObjects']({ fields: '*' });
    }

    static* switchContainer(id: number, selected: boolean): SagaIterator<number[]> {
        let fullContainers = yield* select(getCachedFullContainers);
        let pubContainers = yield* select(getCachedPubContainers);
        const view = yield* select(getOmjsViewInstance);
        let planesData = yield* select(getOperationData, 'GEN_PLANES');
        let status = yield* select(getOperationStatus, 'GEN_PLANES');

        let pubContainer = pubContainers.find(cont => cont.get('id') === id);
        let containers = getChilds(pubContainers, pubContainer, true);
        if (pubContainer) {
            containers.push(pubContainer);
        }

        const inGenPlanes = containers.find(c => (planesData?.allIds || []).includes(c.get('id')));
        if ([ 'gen_plany_koordinaty', 'gen_plans' ].includes(pubContainer?.get('code'))) {
            if (status === EOperationStatus.PENDING || status === EOperationStatus.FAIL) {
                return [];
            }

            if (!planesData) {
                yield* call(OmjsService.getGenPlanesData);
                yield* call(OmjsService.prepareGenPlanes);
                planesData = yield* select(getOperationData, 'GEN_PLANES');
            }
            pubContainers = yield* select(getCachedPubContainers);
            fullContainers = yield* select(getCachedFullContainers);
            pubContainer = pubContainers.find(cont => cont.get('code') === 'gen_plans');
            containers = getChilds(pubContainers, pubContainer, true);
            if (pubContainer) containers.push(pubContainer);
            const { selectedIds } = planesData.planesLayer.options;
            if (!selected) {
                containers.forEach(cont => cont.set('selected', selected));
                if (view.getMap().getView().getZoom() < 12) {
                    yield* call(OperationsService.updateData, 'GEN_PLANES', {
                        selection: Object.keys(planesData.selection)
                            .reduce((acc, key) => ({ ...acc, [key]: false }), {})
                    });
                }
                // eslint-disable-next-line guard-for-in
                for (const code in selectedIds) {
                    selectedIds[code] = [];
                }
            } else {
                let ids;
                const coordsContainer = containers.filter(cont => cont.get('code') === 'gen_plany_koordinaty')[0];

                if (view.getMap().getView().getZoom() < 12) {
                    yield* call(OperationsService.updateData, 'GEN_PLANES', {
                        selection: planesData.defaultSelection
                    });
                    ids = [ pubContainer?.get('id'), coordsContainer.get('id') ];
                    fullContainers.forEach((cont) => {
                        if (ids.indexOf(cont.get('id')) !== -1) {
                            cont.set('selected', selected);
                        }
                    });
                } else {
                    ids = containers
                        .filter(cont => {
                            cont.set('selected', planesData.defaultSelection[cont.get('id')]);
                            return cont.get('selected');
                        })
                        .map(cont => cont.get('id'));

                    const allIds = [ ...planesData.allIds, coordsContainer.get('id'), pubContainer?.get('id') ];
                    fullContainers.forEach((cont) => {
                        if (allIds.indexOf(cont.get('id')) !== -1) {
                            cont.set(
                                'selected',
                                planesData.defaultSelection[cont.get('id')] || cont.get('render_type') === 1
                            );
                        }
                    });
                    ids = [ ...ids, coordsContainer.get('id'), pubContainer?.get('id') ];
                }
                coordsContainer.set('selected', selected);

                // eslint-disable-next-line guard-for-in
                for (const code in planesData.roots) {
                    selectedIds[code] = planesData.roots[code].ids.filter((id_) => planesData.defaultSelection[id_]);
                }
                yield* call(OmjsService.syncProjectAndView);
                yield* call(OmjsService.watchForGenPlanes, { type: '', payload: null });
                return ids;
            }
        } else if (inGenPlanes) {
            let code;
            let ids = [];
            containers.forEach(cont => {
                if (cont.get('type') === 'folder') return;
                for (const key in planesData?.roots) {
                    if (planesData?.roots[key].ids.includes(cont.get('id'))) code = key;
                }
                if (planesData?.roots[code].active) {
                    cont.set('selected', selected);
                    ids.push(cont.get('id'));
                }
            });
            const visibleIds = new Set(planesData.planesLayer.options.selectedIds[code]);
            if (selected) ids.forEach(d => visibleIds.add(d));
            else ids.forEach(d => visibleIds.delete(d));
            planesData.planesLayer.options.selectedIds[code] = Array.from(visibleIds);
        } else {
            containers.forEach(cont => cont.set('selected', selected));
        }

        let parentId = pubContainer?.get('parent_id');
        while (parentId) {
            let parent = getContainerById(pubContainers, parentId);
            parentId = parent.get('parent_id');

            let parentChilds = getChilds(pubContainers, parent);
            let selectedCount = parentChilds.reduce((acc, item) => (item.get('selected') ? acc + 1 : acc), 0);
            if ((selected && selectedCount > 0) || (!selected && selectedCount === 0)) {
                parent.set('selected', selected);
                containers.push(parent);
            }
        }

        const ids = containers.map((cont) => {
            cont.set('selected', selected);
            return cont.get('id');
        });

        fullContainers.forEach((cont) => {
            if (ids.indexOf(cont.get('id')) !== -1) {
                cont.set('selected', selected);
            }
        });

        yield* call(OmjsService.syncProjectAndView);

        return ids;
    }

    static* syncProjectAndView() {
        const omjsView = yield* select(getOmjsViewInstance);

        let withoutLayers = [];

        omjsView['_mapLayersInfo'].forEach((info) => {
            if (info.baseId) {
                withoutLayers = [ ...withoutLayers, ...info.codes ];
            }
        });

        omjsView.getLayers().each((layer) => {
            if (typeof layer['syncWithProject'] === 'function' && !layer.options['baseId']) {
                let res = layer['syncWithProject'](withoutLayers);
                if (res) {
                    if (res.newLayers.length > 0) {
                        layer.show();
                    } else {
                        layer.hide();
                    }
                }
            }
        });

        const mapParams = yield* select(getMapParams);

        yield call(MapService.setActiveLayers,
            mapParams.activeRasterLayers,
            mapParams.activeVectorLayers,
            mapParams.activeGpRasterLayers);
        routes.setMapParams({
            center: mapParams.center,
            zoom: mapParams.zoom,
            layers: [
                ...mapParams.activeRasterLayers,
                ...mapParams.activeVectorLayers,
                ...mapParams.activeGpRasterLayers ].join(',')
        });
    }

    static* getActiveLayers() {
        const pubContainers = yield* select(getCachedPubContainers);

        const activeRasterLayers = [];
        const activeVectorLayers = [];
        const lTypes = [ 'layer', 'virtual', 'raster' ];

        pubContainers.forEach((c) => {
            if (!!~lTypes.indexOf(c.get('type')) && c.get('selected')) {
                if (c.get('render_type') === 1) {
                    activeVectorLayers.push(c.get('id'));
                } else {
                    activeRasterLayers.push(c.get('id'));
                }
            }
        });

        return [ activeRasterLayers, activeVectorLayers ];
    }

    static* getMapPosition() {
        const omjsView = yield* select(getOmjsViewInstance);
        const mapView = omjsView.getMap().getView();
        let center = omjs.ol.proj.toLonLat(mapView.getCenter());
        center = [ center[0].toFixed(6), center[1].toFixed(6) ].join(',');
        const zoom = Math.round(omjs.Utils.getZoomFromResolution(mapView.getResolution()));
        return { center, zoom };
    }

    /* Gen planes */
    static async loadGenPlanesConts(url: string) {
        return axios.get(url);
    }

    @operation('GEN_PLANES')
    static* getGenPlanesData(/* pubContainers: omjs.Container[], fullContainers: omjs.Container[] */) {
        const pubContainers = yield* select(getCachedPubContainers);
        const fullContainers = yield* select(getCachedFullContainers);
        const pubParent = pubContainers.find(cont => cont.get('code') === 'gen_plans');
        const fullParent = fullContainers.find(cont => cont.get('code') === 'gen_plans');
        const { config, authToken } = yield* select((state: IState) => state.app);
        let allIds = [];
        let selection = {};
        const view = yield* select((state: IState) => state.omjs.omjsViewInstance);

        let { data: gpConfig } = yield* call([ apiRequest, apiRequest.getGpConfig ], authToken || '');

        /*
        const idsMap = getChilds(fullContainers, fullParent, true)
            .filter(c => c.get('type') !== 'folder')
            .map(c => ({ id: c.get('id'), code: c.get('code') }));

        let dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(idsMap))}`;
        let dlAnchorElem = document.createElement('a');
        document.body.appendChild(dlAnchorElem);
        dlAnchorElem.setAttribute('href', dataStr);
        dlAnchorElem.setAttribute('download', 'old.json');
        dlAnchorElem.click();
        */

        config.publication.layers_config.push.apply(
            config.publication.layers_config,
            gpConfig.publication.layers_config
        );

        const gpProject = new omjs.Project({
            apiUrl: view.getProject().options.apiUrl,
            mapCode: 'gen_plans'
        });


        const [ gpPubContainers, gpFullContainers ] = yield* all([
            call(OmjsService.projectGetContainers, gpProject, {
                type: 'publish',
                returnBbox: 0,
                bboxSR: 3857,
                with_gen_plans: 1
            }),
            call(OmjsService.projectGetContainers, gpProject, {
                type: 'full',
                returnBbox: 0,
                returnCss: 0,
                with_gen_plans: 1
            })
        ]) as unknown as [omjs.ContainersCollection, omjs.ContainersCollection];

        gpPubContainers.getArray().forEach(c => {
            if (c.get('parent_id') === 0) {
                c.set('parent_id', pubParent.get('id'));
            }
        });
        pubContainers.push.apply(pubContainers, gpPubContainers.getArray());

        gpFullContainers.getArray().forEach(c => {
            if (c.get('parent_id') === 0) {
                c.set('parent_id', fullParent.get('id'));
            }
        });
        fullContainers.push.apply(fullContainers, gpFullContainers.getArray());

        (view.getProject() as any)._cache.publishContainers.reset(pubContainers);

        yield* call(ContainersService.init, view.getProject().getCachedPubContainers());

        const roots = getChilds(pubContainers, pubParent).filter((cont) =>
            // if (cont.get('type') === 'layer') {
            //     points = cont;
            // }
            cont.get('type') !== 'layer');

        roots.forEach((root) => {
            const layers = getChilds(pubContainers, root, true).filter(child => child.get('type') !== 'folder');
            const ids = layers.map(child => child.get('id'));
            root.ids = ids;

            allIds = [ ...allIds, ...ids ];
            layers.forEach((layer) => {
                selection[layer.get('id')] = layer.get('selected');
            });
        });

        fullContainers.forEach((cont) => {
            if (allIds.includes(cont.get('id'))) {
                cont.set('selected', false);
            }
        });

        yield* call(OperationsService.setData, 'GEN_PLANES', {
            allIds,
            selection,
            defaultSelection: selection,
            roots: roots.reduce((acc, item) => {
                acc[item.get('code')] = {
                    active: false,
                    ids: item.ids
                };
                return acc;
            }, {})
        });
    }

    @leading
    @operation('GEN_PLANES')
    static* prepareGenPlanes() {
        const omjsView = yield* select(getOmjsViewInstance);
        const planesData = yield select(getOperationData, 'GEN_PLANES');
        const pubContainers = yield* select(getCachedPubContainers);
        const fullContainers = yield* select(getCachedFullContainers);

        let planesLayer;
        const viewLayers = omjsView.getLayers();

        viewLayers.each((layer) => {
            const prjLayers = layer.getProjectLayers();
            if (prjLayers && prjLayers.getLength() > 1) {
                const layers_ids = prjLayers._items.map(it => it.get('id'));

                if ((_.intersection(planesData.allIds, layers_ids)).length) {
                    planesLayer = layer;
                }
            }
        });

        if (!planesLayer) {
            const settlementsContainer = fullContainers.find(c => c.get('code') === 'gen_plany_koordinaty');
            const settlementsLayer = viewLayers.getArray().find(layer => {
                if (layer instanceof omjs.View.OmsVectorLayer) {
                    const cont = layer.getProjectLayers().at(0);
                    return cont && cont.get('code') === 'gen_plany_koordinaty';
                } else {
                    return false;
                }
            });

            const roots = pubContainers.filter(c => Object.keys(planesData.roots).includes(c.get('code')));

            const mapLayers = omjsView.getMap().getLayers().item(1);

            let planesChilds = [];
            const rootsIds = {};
            for (const root of roots) {
                const childs = getChilds(fullContainers, root, true)
                    .filter(c => c.get('type') !== 'folder')
                    .map((ch) => {
                        ch.set('root_code', root.get('code'));
                        return ch;
                    });
                planesChilds = planesChilds.concat(childs);
                rootsIds[root.get('code')] = childs
                    .filter((ch) => planesData.defaultSelection[ch.get('id')])
                    .map((ch) => ch.get('id'));
            }
            planesLayer = new omjs.View.TileLayer({
                projectLayers: new omjs.Project.ContainersCollection(planesChilds, {}),
                urlTemplate: omjsView.getProject().options.tilesUrl,
                mapCode: 'gen_plans',
                visible: false,
                tilesQuality: 2,
                selectedIds: rootsIds
            });
            planesLayer.getMapLayer().set('gpLayer', true);

            omjsView.getLayers().getArray().splice(
                omjsView.getLayers().getArray().indexOf(settlementsLayer),
                0,
                planesLayer
            );

            omjsView.getLayers().reset([ ...omjsView.getLayers().getArray() ]);

            mapLayers.changed();
        }

        let { apiUrl } = omjsView.getProject().options;
        const { data } = yield call(
            OmjsService.loadGenPlanesConts,
            `${apiUrl}gen_plans/layers/bbox/objects/?returnBbox=1&bboxSR=3857&`
        );
        const planesExtents = data.map(l => (
            {
                extent: [
                    Math.min(l.bbox.ne[1], l.bbox.sw[1]),
                    Math.min(l.bbox.ne[0], l.bbox.sw[0]),
                    Math.max(l.bbox.ne[1], l.bbox.sw[1]),
                    Math.max(l.bbox.ne[0], l.bbox.sw[0])
                ],
                id: l.id
            }
        ));

        yield* call(OperationsService.updateData, 'GEN_PLANES', {
            planesExtents,
            planesLayer
        });

        // yield call(OmjsService.watchForGenPlanes, action);
    }

    @every
    static* watchForGenPlanes(action: IAction<{ zoom: number, center: number[] } | null>) {
        const pubContainers = yield* select(getCachedPubContainers);
        const omjsView = yield* select(getOmjsViewInstance);
        const mapView = omjsView.getMap().getView();
        const settlements = pubContainers.find(cont => cont.get('code') === 'gen_plany_koordinaty');
        let zoom = action.payload ? action.payload.zoom : mapView.getZoom();
        let planesData = yield select(getOperationData, 'GEN_PLANES');
        const status = yield select(getOperationStatus, 'GEN_PLANES');

        if (status === EOperationStatus.PENDING || status === EOperationStatus.FAIL) {
            return;
        }

        if (settlements.get('selected') && !planesData && zoom >= 12) {
            yield call(OmjsService.getGenPlanesData);
            yield call(OmjsService.prepareGenPlanes);
            planesData = yield select(getOperationData, 'GEN_PLANES');
        }

        if (planesData === null) {
            return;
        }

        if (zoom >= 12) {
            if (planesData.selection) {
                yield call(OmjsService.restorePlansSelected);
            }
            const extent = mapView.calculateExtent();

            const intersected = planesData.planesExtents
                .filter(ext => omjs.ol.extent.intersects(ext.extent, extent))
                .map(ext => ext.id);

            for (let key of Object.keys(planesData.roots)) {
                let intersected_ids = _.intersection(intersected, planesData.roots[key].ids);
                planesData.roots[key].active = intersected_ids.length > 0;
            }
            const { activeGpRasterLayers } = yield select((state) => getOperationData(state, 'MAP_STATE'));
            let selected = false;
            // eslint-disable-next-line guard-for-in
            for (const key in planesData.roots) {
                if (planesData.roots[key].active && !selected) {
                    selected = planesData.roots[key].ids.find(id => activeGpRasterLayers.includes(id));
                }
            }

            for (const cont of planesData.planesLayer._prjLayers.getArray()) {
                const folder = cont.get('root_code');

                const selectedCont = planesData.roots[folder].active
                    && planesData.planesLayer.options.selectedIds[folder].includes(cont.get('id'));
                cont.set('selected', selectedCont);
            }

            if (selected) {
                planesData.planesLayer.show();
            } else {
                planesData.planesLayer.hide();
            }
            yield call(OmjsService.syncProjectAndView);
            yield* call(OperationsService.updateData, 'GEN_PLANES', {
                roots: { ...planesData.roots }
            });
        } else {
            for (let key of Object.keys(planesData.roots)) {
                planesData.roots[key].active = false;
            }

            yield* call(OperationsService.updateData, 'GEN_PLANES', {
                roots: { ...planesData.roots }
            });
            if (!planesData.selection) {
                yield call(OmjsService.storePlansSelected);
            }
        }
    }

    static* storePlansSelected() {
        const { allIds, planesLayer } = yield select(getOperationData, 'GEN_PLANES');
        const pubContainers = yield* select(getCachedPubContainers);
        const containers = yield select(getOperationData, 'CONTAINERS');
        const selection = {};

        const { selectedIds } = planesLayer.options;
        planesLayer._prjLayers.each((cont) => {
            if (allIds.includes(cont.get('id'))) {
                selection[cont.get('id')] = selectedIds[cont.get('root_code')].includes(cont.get('id'));
                cont.set('selected', false);
            }
        });

        for (let cont of pubContainers) {
            if (allIds.includes(cont.get('id'))) {
                cont.set('selected', false);
            }
        }

        yield* call(OperationsService.updateData, 'GEN_PLANES', {
            selection
        });

        yield call(OmjsService.syncProjectAndView);

        yield* call(OperationsService.updateData, 'CONTAINERS', containers.map((cont) => {
            if (allIds.includes(cont.id)) {
                cont.selected = false;
            }
            return cont;
        }));

        yield call(ContainersService.updateParentSelected);
    }

    static* restorePlansSelected() {
        const { selection, allIds, planesLayer, roots } = yield select(getOperationData, 'GEN_PLANES');
        const pubContainers = yield* select(getCachedPubContainers);
        const containers = yield select(getOperationData, 'CONTAINERS');

        planesLayer._prjLayers.each((cont) => {
            if (allIds.includes(cont.get('id')) && roots[cont.get('root_code')].active) {
                cont.set('selected', selection[cont.get('id')]);
            }
        });

        pubContainers.forEach((cont) => {
            if (cont.get('id') in selection) {
                cont.set('selected', selection[cont.get('id')]);
            }
        });

        yield call(OmjsService.syncProjectAndView);
        yield* call(OperationsService.updateData, 'CONTAINERS', containers.map((cont) => {
            if (cont.id in selection) {
                cont.selected = selection[cont.id];
            }
            return cont;
        }));

        yield* call(OperationsService.updateData, 'GEN_PLANES', {
            selection: false
        });

        yield call(ContainersService.updateParentSelected);
    }


    // Легенды
    static async loadLegendData(url) {
        return axios.get(url);
    }

    @every
    static* loadLegend(action: IAction<string>) {
        const omjsView = yield* select(getOmjsViewInstance);
        const containers = yield* select(getCachedFullContainers);
        let { apiUrl, mapCode } = omjsView.getProject().options;

        const structure = yield* call(OmjsService.loadProjectLayerStructure, action.payload);

        const { data } = yield call(
            OmjsService.loadLegendData,
            `${apiUrl}${mapCode}/layers/${action.payload}/legend/?lng=${i18n.language}`
        );

        // console.log(data);

        const container = containers.find(cont => cont.get('code') === action.payload);
        container['_legend'] = data;
        container['structure'] = structure;
    }
}

export const omjsService: OmjsService = new OmjsService();
export const actions = bindActionsTyped(OmjsService, omjsService);
